import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import md5 from 'md5';
import { useApiFieldErrors, useI18n, useMixpanel } from '@/composables';
import { $emitter } from '@/services/emitter';
import api, { type BotFiltersApi } from '@/views/settings/bot-filters/api';
import { useStore } from '@/store';
import {
  parseFlowErrorsRaw,
  translateFlowErrorsRaw,
} from '@/utils/parseApiErrors';

type AddBotDetectionRulesData = {
  id?: string;
};

type BotRuleForm = {
  rules: Record<string, unknown>;
  name: string;
  logicalRelation: string;
  status: string;
};

export const useAddBotDetectionRules = (
  data: AddBotDetectionRulesData,
  emitClose: () => void,
  emitSave: () => void,
) => {
  const { t, locale } = useI18n();
  const store = useStore();
  const { trackMixpanel } = useMixpanel();
  const botFiltersApi = api as BotFiltersApi;

  const { errorsDict, applyApiErrors } = useApiFieldErrors();
  const errorsControlActive = ref(false);
  const errorsRaw = ref<unknown[][] | null>(null);
  const errorMessage = ref('');

  const show = ref(false);
  const isLoadingDictionary = ref(false);
  const activeName = ref('');
  const activeId = ref('');
  const activeRule = ref<{ conditions: Record<string, unknown> }>({ conditions: {} });
  const relation = ref('and');
  const oldHash = ref('');

  const aggregatedErrors = computed(() =>
    translateFlowErrorsRaw(errorsRaw.value, errorMessage.value),
  );

  const getDefaultActiveRule = () => ({
    conditions: {},
  });

  const getForm = (): BotRuleForm => ({
    rules: activeRule.value.conditions,
    name: activeName.value,
    logicalRelation: relation.value,
    status: 'active',
  });

  const getCurrentHash = () => md5(JSON.stringify(getForm()));

  const trackAnalytics = (event: string, form: BotRuleForm) => {
    const rules =
      Object.keys(form.rules).reduce<string[]>((accumulator, rule) => {
        accumulator.push(rule);
        return accumulator;
      }, []) || [];

    trackMixpanel(event, {
      name: form.name,
      rules,
    });
  };

  const close = () => {
    emitClose();
  };

  const checkClose = () => {
    if (oldHash.value !== getCurrentHash()) {
      $emitter.emit('open-dialog', {
        name: 'ConfirmDialog',
        data: { onConfirm: () => close() },
      });
    } else {
      close();
    }
  };

  const applySubmitErrors = (error: unknown) => {
    const responseData = (error as { response?: { data?: { errors?: unknown; message?: string } } })
      ?.response?.data;

    if (responseData?.errors) {
      errorsRaw.value = parseFlowErrorsRaw(responseData.errors);
      errorMessage.value = responseData.message || '';
      errorsControlActive.value = true;
    }
  };

  const save = async () => {
    if (isLoadingDictionary.value) return;

    const form = getForm();
    store.setLoading(true);
    botFiltersApi.loading = true;

    try {
      if (!activeId.value) {
        await botFiltersApi.post(form);
        await botFiltersApi.get();
        emitSave();
        trackAnalytics('bot_detection_rules_create', form);
        close();
        $emitter.emit('toast-success', t('Bot rule has been successfully added'));
      } else {
        await botFiltersApi.putRules(activeId.value, form);
        await botFiltersApi.get();
        emitSave();
        trackAnalytics('bot_detection_rules_edit', form);
        close();
        $emitter.emit('toast-success', t('Bot rule has been changed'));
      }
    } catch (error) {
      applyApiErrors(error);
      applySubmitErrors(error);
    } finally {
      store.setLoading(false);
      botFiltersApi.loading = false;
    }
  };

  const onSaveDictionary = (isLoading: boolean) => {
    isLoadingDictionary.value = isLoading;
  };

  const open = () => {
    const { id } = data;
    store.setLoading(true);

    if (id) {
      botFiltersApi.getById(id).then((response: { rules: Record<string, unknown>; name: string; id: string; logicalRelation?: string }) => {
        activeRule.value.conditions = response.rules;
        activeName.value = response.name;
        activeId.value = response.id;
        relation.value = response.logicalRelation || 'and';
        show.value = true;
        oldHash.value = getCurrentHash();
        store.setLoading(false);
      });
    } else {
      activeRule.value = getDefaultActiveRule();
      activeName.value = '';
      activeId.value = '';
      relation.value = 'and';
      show.value = true;
      oldHash.value = getCurrentHash();
      store.setLoading(false);
    }
  };

  onMounted(() => {
    $emitter.on('loading-add-dictionary', onSaveDictionary);
    open();
  });

  onBeforeUnmount(() => {
    $emitter.off('loading-add-dictionary', onSaveDictionary);
  });

  return {
    t,
    locale,
    show,
    activeName,
    activeId,
    activeRule,
    relation,
    errors: aggregatedErrors,
    errorsControlActive,
    errorsDict,
    botFiltersApi,
    checkClose,
    save,
  };
};
